import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Package, AlertTriangle, CheckCircle, XCircle, Save, RefreshCw, Search } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { resolveImageUrl, getMainImage } from '../utils/imageUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryAdminPage = () => {
  const { toast } = useToast();
  const [garments, setGarments] = useState([]);
  const [inventory, setInventory] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCollection, setSelectedCollection] = useState('all');
  const [summary, setSummary] = useState(null);
  const [changes, setChanges] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  // Avisar al usuario si intenta cerrar la pestaña con cambios sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (Object.keys(changes).length > 0) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [changes]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch garments and inventory in parallel
      const [garmentsRes, inventoryRes, summaryRes] = await Promise.all([
        axios.get(`${API}/garments`),
        axios.get(`${API}/inventory`),
        axios.get(`${API}/inventory/summary`)
      ]);
      
      setGarments(garmentsRes.data);
      setSummary(summaryRes.data);
      
      // Convert inventory array to lookup object
      const invMap = {};
      inventoryRes.data.forEach(item => {
        const key = `${item.garment_id}_${item.size}_${item.color}`;
        invMap[key] = item.quantity;
      });
      setInventory(invMap);
      setChanges({});
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el inventario',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStockKey = (garmentId, size, color) => `${garmentId}_${size}_${color}`;

  const getStockValue = (garmentId, size, color) => {
    const key = getStockKey(garmentId, size, color);
    if (changes[key] !== undefined) return changes[key];
    return inventory[key] || 0;
  };

  const handleStockChange = (garmentId, size, color, value) => {
    const key = getStockKey(garmentId, size, color);
    const numValue = Math.max(0, parseInt(value) || 0);
    setChanges(prev => ({
      ...prev,
      [key]: numValue
    }));
  };

  const saveChanges = async () => {
    if (Object.keys(changes).length === 0) {
      toast({
        title: 'Sin cambios',
        description: 'No hay cambios para guardar'
      });
      return;
    }

    setSaving(true);
    try {
      // Convert changes to bulk update format
      const items = Object.entries(changes).map(([key, quantity]) => {
        const [garment_id, size, color] = key.split('_');
        return { garment_id, size, color, quantity };
      });

      await axios.post(`${API}/inventory/bulk`, items);
      
      // Update local inventory state
      setInventory(prev => ({
        ...prev,
        ...changes
      }));
      setChanges({});
      
      // Refresh summary
      const summaryRes = await axios.get(`${API}/inventory/summary`);
      setSummary(summaryRes.data);

      toast({
        title: '¡Guardado!',
        description: `Se actualizaron ${items.length} items`
      });
    } catch (error) {
      console.error('Error saving:', error);
      toast({
        title: 'Error',
        description: 'No se pudo guardar el inventario',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) return { status: 'out', label: 'Agotado', color: 'bg-red-100 text-red-700' };
    if (quantity <= 3) return { status: 'low', label: 'Pocas', color: 'bg-yellow-100 text-yellow-700' };
    return { status: 'ok', label: 'OK', color: 'bg-green-100 text-green-700' };
  };

  const filteredGarments = garments.filter(g => {
    const matchesSearch =
      g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.gender.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCollection =
      selectedCollection === 'all' ||
      (g.collection || '').toUpperCase().includes(selectedCollection.toUpperCase());
    return matchesSearch && matchesCollection;
  });

  // Colecciones únicas para el selector
  const availableCollections = [...new Set(garments.map((g) =>
    (g.collection || '').replace(/^COLLECTION\s+/i, '').toUpperCase().trim()
  ).filter(Boolean))].sort((a, b) => {
    const order = ['LOTUS', 'TULIPS'];
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });

  // Agrupar por colección para orden premium
  const groupedByCollection = filteredGarments.reduce((acc, g) => {
    const coll = (g.collection || 'SIN COLECCIÓN').replace(/^COLLECTION\s+/i, '').toUpperCase();
    if (!acc[coll]) acc[coll] = [];
    acc[coll].push(g);
    return acc;
  }, {});

  // Ordenar: LOTUS primero, TULIPS después, resto alfabético
  const collectionOrder = ['LOTUS', 'TULIPS'];
  const sortedCollectionKeys = Object.keys(groupedByCollection).sort((a, b) => {
    const ia = collectionOrder.indexOf(a);
    const ib = collectionOrder.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });

  const hasChanges = Object.keys(changes).length > 0;

  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Header */}
      <div className="bg-black text-white">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link 
            to="/admin/orders" 
            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
            Volver a pedidos
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-400 mb-2">
                Admin
              </p>
              <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter leading-none">
                Gestión de inventario
              </h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchData}
                className="bg-transparent border border-white text-white hover:bg-white hover:text-black px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" strokeWidth={2} />
                Actualizar
              </button>
              <button
                onClick={saveChanges}
                disabled={!hasChanges || saving}
                className="bg-white text-black hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors flex items-center gap-2"
                data-testid="save-inventory"
              >
                <Save className="w-4 h-4" strokeWidth={2} />
                {saving ? 'Guardando...' : `Guardar${hasChanges ? ` (${Object.keys(changes).length})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            <div className="border border-neutral-200 p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1">Total unidades</p>
              <p className="text-3xl font-black">{summary.total_items}</p>
            </div>
            <div className="border border-neutral-200 p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1">Agotados</p>
              <p className="text-3xl font-black">{summary.out_of_stock_count}</p>
            </div>
            <div className="border border-neutral-200 p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1">Stock bajo</p>
              <p className="text-3xl font-black">{summary.low_stock_count}</p>
            </div>
            <div className="border border-neutral-200 p-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500 mb-1">Productos</p>
              <p className="text-3xl font-black">{garments.length}</p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" strokeWidth={1.5} />
            <input
              placeholder="Buscar producto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-6 pr-4 py-2 text-sm bg-transparent border-b border-neutral-300 focus:outline-none focus:border-black transition-colors placeholder-neutral-400"
            />
          </div>
        </div>

        {/* Filtro rápido por colección */}
        {availableCollections.length > 1 && (
          <div className="mb-8" data-testid="collection-filter-buttons">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">
              Filtrar por colección
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCollection('all')}
                className={`px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] transition-all border ${
                  selectedCollection === 'all'
                    ? 'bg-black text-white border-black'
                    : 'bg-white text-black border-neutral-300 hover:border-black'
                }`}
                data-testid="inv-collection-all"
              >
                Todas ({garments.length})
              </button>
              {availableCollections.map((coll) => {
                const count = garments.filter((g) =>
                  (g.collection || '').toUpperCase().includes(coll)
                ).length;
                const isActive = selectedCollection.toUpperCase() === coll;
                return (
                  <button
                    key={coll}
                    onClick={() => setSelectedCollection(coll)}
                    className={`px-6 py-3 text-[11px] font-bold uppercase tracking-[0.2em] transition-all border ${
                      isActive
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-black border-neutral-300 hover:border-black'
                    }`}
                    data-testid={`inv-collection-${coll.toLowerCase()}`}
                  >
                    {coll} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Inventory Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-24">
            <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="space-y-12">
            {sortedCollectionKeys.map((collectionName) => (
              <section key={collectionName} data-testid={`collection-group-${collectionName.toLowerCase()}`}>
                {/* Encabezado editorial de colección */}
                <div className="mb-6 pb-4 border-b-2 border-black">
                  <div className="flex items-baseline justify-between gap-4 flex-wrap">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-1">
                        Colección
                      </p>
                      <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter leading-none">
                        {collectionName}
                      </h2>
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-500">
                      {groupedByCollection[collectionName].length} {groupedByCollection[collectionName].length === 1 ? 'prenda' : 'prendas'}
                    </span>
                  </div>
                </div>

                {/* Tarjetas del inventario de esta colección */}
                <div className="space-y-3">
                  {groupedByCollection[collectionName].map((garment) => (
                    <div key={garment.id} className="border border-neutral-200 p-4 bg-white" data-testid={`inventory-${garment.id}`}>
                      <div className="flex flex-col lg:flex-row gap-4">
                        {/* Product Info */}
                        <div className="flex items-center gap-4 lg:w-64 flex-shrink-0">
                          <img
                            src={resolveImageUrl(getMainImage(garment))}
                            alt={garment.name}
                            className="w-16 h-20 object-cover bg-neutral-100"
                          />
                          <div>
                            <h3 className="font-bold text-sm uppercase text-black leading-tight">{garment.name}</h3>
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500 mt-1">
                              {garment.gender}
                            </p>
                          </div>
                        </div>

                        {/* Stock Grid */}
                        <div className="flex-1 overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-neutral-200">
                                <th className="text-left py-2 px-2 text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">Color</th>
                                {sizes.map(size => (
                                  <th key={size} className="text-center py-2 px-2 text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500 w-16">
                                    {size}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {(garment.colors && garment.colors.length > 0 ? garment.colors : ['Color único']).map(color => (
                                <tr key={color} className="border-b border-neutral-100 last:border-0">
                                  <td className="py-2 px-2 text-black whitespace-nowrap text-sm">{color}</td>
                                  {sizes.map(size => {
                                    const value = getStockValue(garment.id, size, color);
                                    const key = getStockKey(garment.id, size, color);
                                    const hasChange = changes[key] !== undefined;

                                    return (
                                      <td key={size} className="py-2 px-1 text-center">
                                        <input
                                          type="number"
                                          min="0"
                                          value={value}
                                          onChange={(e) => handleStockChange(garment.id, size, color, e.target.value)}
                                          className={`w-14 h-9 text-center text-sm border transition-colors focus:outline-none ${
                                            value === 0 ? 'border-neutral-200 text-neutral-400' :
                                            'border-black text-black font-semibold'
                                          } ${hasChange ? 'bg-black text-white' : 'bg-white'} focus:border-black`}
                                          data-testid={`stock-${garment.id}-${size}-${color}`}
                                        />
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Legend */}
        <div className="mt-8 flex flex-wrap gap-6 text-xs text-neutral-500">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border border-black bg-white"></div>
            <span>Con stock</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border border-neutral-200 bg-white"></div>
            <span>Agotado (0)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-black"></div>
            <span>Modificado (sin guardar)</span>
          </div>
        </div>
      </div>

      {/* Sticky Save Bar (aparece cuando hay cambios sin guardar) */}
      {hasChanges && (
        <div className="fixed bottom-0 inset-x-0 z-[9999] bg-black text-white shadow-2xl animate-slideUp" data-testid="sticky-save-bar">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <p className="text-xs font-bold uppercase tracking-[0.2em]">
                {Object.keys(changes).length} {Object.keys(changes).length === 1 ? 'cambio sin guardar' : 'cambios sin guardar'}
              </p>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <button
                onClick={() => setChanges({})}
                className="flex-1 sm:flex-initial bg-transparent border border-white text-white hover:bg-white hover:text-black px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors"
                data-testid="discard-changes"
              >
                Descartar
              </button>
              <button
                onClick={saveChanges}
                disabled={saving}
                className="flex-1 sm:flex-initial bg-white text-black hover:bg-neutral-200 disabled:opacity-60 px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2"
                data-testid="sticky-save-btn"
              >
                <Save className="w-4 h-4" strokeWidth={2} />
                {saving ? 'Guardando...' : `Guardar (${Object.keys(changes).length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>

      <Toaster />
    </div>
  );
};

export default InventoryAdminPage;
